import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star, Shield, Award, Users } from 'lucide-react';

const TrustCredibility = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Priya Sharma',
      age: 34,
      location: 'Mumbai',
      rating: 5,
      text: 'I was skeptical at first, but after my first cleanse, I felt so light and energetic! My digestion improved dramatically. The therapist was so professional and caring.',
      image: '👩🏻',
      result: 'Lost 3kg in 2 weeks',
    },
    {
      name: 'Rahul Mehta',
      age: 41,
      location: 'Bangalore',
      rating: 5,
      text: 'Struggled with bloating for years. After 3 sessions, it is completely gone. The herbal formulas are gentle yet incredibly effective. Highly recommend!',
      image: '👨🏻',
      result: 'No more bloating',
    },
    {
      name: 'Anjali Desai',
      age: 29,
      location: 'Delhi',
      rating: 5,
      text: 'Best decision for my health! My skin is glowing, energy levels are through the roof, and I sleep so much better. The therapist explained everything clearly.',
      image: '👩🏽',
      result: 'Better sleep & energy',
    },
    {
      name: 'Vikram Singh',
      age: 38,
      location: 'Pune',
      rating: 5,
      text: 'As someone with a busy lifestyle, I needed something that worked without disrupting my schedule. The 16-hour process fit perfectly. Results were amazing!',
      image: '👨🏾',
      result: 'Improved metabolism',
    },
  ];

  const trustBadges = [
    {
      icon: Shield,
      title: 'Certified Therapists',
      description: '100% trained & verified',
      color: 'from-blue-400 to-cyan-400',
    },
    {
      icon: Users,
      title: '5000+ Happy Clients',
      description: 'Across 12 cities',
      color: 'from-purple-400 to-pink-400',
    },
    {
      icon: Award,
      title: 'Zero Side Effects',
      description: '100% natural ingredients',
      color: 'from-green-400 to-emerald-400',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div id="trust-zone" className="py-20 px-4 bg-gradient-to-br from-white via-purple-50 to-blue-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Trusted by Thousands
          </h2>
          <p className="text-xl text-gray-600">
            Real people. Real results. Real transformations.
          </p>
        </motion.div>

        {/* Trust Badges */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {trustBadges.map((badge, index) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${badge.color} flex items-center justify-center mb-4 mx-auto`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                  {badge.title}
                </h3>
                <p className="text-gray-600 text-center">
                  {badge.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative bg-white rounded-3xl p-8 md:p-12 shadow-2xl overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-purple-400 to-pink-400" />

          <AnimatePresence mode="wait">
            <motion.div
              key={currentTestimonial}
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              {/* User Avatar */}
              <div className="text-7xl mb-6">
                {testimonials[currentTestimonial].image}
              </div>

              {/* Rating Stars */}
              <div className="flex justify-center gap-1 mb-4">
                {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-xl text-gray-700 mb-6 max-w-3xl mx-auto leading-relaxed">
                "{testimonials[currentTestimonial].text}"
              </p>

              {/* Result Badge */}
              <div className="inline-block bg-gradient-to-r from-primary/10 to-purple-100 text-primary px-6 py-2 rounded-full font-semibold mb-6">
                ✨ {testimonials[currentTestimonial].result}
              </div>

              {/* User Info */}
              <div>
                <p className="font-bold text-gray-800 text-lg">
                  {testimonials[currentTestimonial].name}, {testimonials[currentTestimonial].age}
                </p>
                <p className="text-gray-600">
                  {testimonials[currentTestimonial].location}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={prevTestimonial}
              className="w-12 h-12 bg-primary/10 hover:bg-primary hover:text-white text-primary rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft className="w-6 h-6" />
            </motion.button>

            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentTestimonial
                      ? 'bg-primary w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={nextTestimonial}
              className="w-12 h-12 bg-primary/10 hover:bg-primary hover:text-white text-primary rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight className="w-6 h-6" />
            </motion.button>
          </div>
        </motion.div>

        {/* Social Proof Counter */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-gray-600 text-lg">
            Join <span className="font-bold text-primary">5,247 people</span> who trusted us this month
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TrustCredibility;
